import { useEffect, useState } from 'react'
import { useAppAuth } from '@/contexts/app-auth.context'
import { directoryApiClient } from '@/lib/api/directory.client'
import { directoryAdminApiClient } from '@/lib/api/directory-admin.client'
import { propertiesApiClient } from '@/lib/api/properties.client'
import { getPropertyDisplayLabel } from '@/lib/utils/handoff-form-utils'
import type { RoutingState } from '@super/node-graph-ui'

/**
 * The company's properties and property groups, for the arrow conditions that
 * name them. Super admins read the company the switcher selects; everyone else
 * reads their session company. Undefined with no company.
 */
export function useRoutingOptions(): RoutingState | undefined {
  const { currentCompany, isSuperAdmin } = useAppAuth()
  const companyId = currentCompany?.id
  const [state, setState] = useState<RoutingState>('loading')

  useEffect(() => {
    if (!companyId) return
    let cancelled = false
    setState('loading')
    Promise.all([
      isSuperAdmin
        ? propertiesApiClient.getAllAdminPropertiesForCompany(companyId)
        : propertiesApiClient.getProperties(),
      isSuperAdmin
        ? directoryAdminApiClient.listPropertyGroups(companyId)
        : directoryApiClient.listPropertyGroups(),
    ])
      .then(([properties, groups]) => {
        if (cancelled) return
        setState({
          properties: properties.map(property => ({
            value: property.id,
            label: getPropertyDisplayLabel(property),
          })),
          propertyGroups: groups.map(group => ({
            value: group.id,
            label: group.name,
          })),
        })
      })
      .catch(error => {
        console.error('Failed to load properties and property groups', error)
        if (!cancelled) setState('failed')
      })
    return () => {
      cancelled = true
    }
  }, [companyId, isSuperAdmin])

  return companyId ? state : undefined
}
